import { FullList, ProblemDetails } from "../models/Http";
import { get, post, put, del, upload } from "./api";
import {
  StoreInventoryItem,
  StoreInventoryItemWriteModel,
  ProductSpoilageReport,
  MinimalStoreInventoryItem
} from "../models/StoreInventory";
import { ProductId } from "../models/Product";
import { err, ok, Result, Unit } from "../result";
import moment from "moment";

export const list = async (
  storeId: string
): Promise<Result<ProblemDetails, ReadonlyArray<StoreInventoryItem>>> => {
  const response = await get(`/v1/stores/${storeId}/products`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StoreInventoryItem>);
  }

  return err(content as ProblemDetails);
};

export const listWithStatistics = async (
  storeId: string,
  filters: any
): Promise<Result<ProblemDetails, ReadonlyArray<StoreInventoryItem>>> => {
  const { categoryIds, distributorIds, brandNames, dateRange } = filters;

  let rest = "";

  if (categoryIds && categoryIds.length)
    categoryIds.forEach((val: any) => {
      rest = `${rest}&categoryIds=${val}`;
    });

  if (distributorIds && distributorIds.length)
    distributorIds.forEach((val: any) => {
      rest = `${rest}&distributorIds=${val}`;
    });

  if (brandNames && brandNames.length)
    brandNames.forEach((val: any) => {
      rest = `${rest}&brandNames=${val}`;
    });

  if (dateRange && dateRange.length === 2)
    rest = `?periodFrom=${moment(dateRange[0]).format(
      "YYYY-MM-DD"
    )}&periodTo=${moment(dateRange[1]).format("YYYY-MM-DD")}${rest}`;

  const response = await get(
    `/v1/stores/${storeId}/products/statistics${rest}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StoreInventoryItem>);
  }

  return err(content as ProblemDetails);
};

export const listAll = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<MinimalStoreInventoryItem>>
> => {
  const response = await get("/v1/stores/inventory-items");
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<MinimalStoreInventoryItem>);
  }

  return err(content as ProblemDetails);
};

export const search = async (
  storeId: string,
  searchPhrase: string
): Promise<
  Result<ProblemDetails, ReadonlyArray<MinimalStoreInventoryItem>>
> => {
  const response = await get(`/v1/stores/${storeId}/products/search`, {
    searchPhrase
  });
  const content = await response.json();

  if (response.ok) {
    const list = content as FullList<MinimalStoreInventoryItem>;
    return ok(list.items);
  }

  return err(content as ProblemDetails);
};

export const getSingle = async (
  storeId: string,
  productId: ProductId
): Promise<Result<ProblemDetails, StoreInventoryItem>> => {
  const response = await get(`/v1/stores/${storeId}/products/${productId}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreInventoryItem);
  }

  return err(content as ProblemDetails);
};

export const getSingleByGTIN = async (
  storeId: string,
  gtin: string
): Promise<Result<ProblemDetails, StoreInventoryItem>> => {
  const response = await get(`/v1/stores/${storeId}/products/${gtin}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreInventoryItem);
  }

  return err(content as ProblemDetails);
};

export const getSingleByGTINWithNoReports = async (
  storeId: string,
  gtin: string
): Promise<Result<ProblemDetails, StoreInventoryItem>> => {
  const response = await get(
    `/v1/stores/${storeId}/products/${gtin}/no-reports`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreInventoryItem);
  }

  return err(content as ProblemDetails);
};

export const getDeliveryStockTakingAndSpoilage = async (
  storeId: string,
  productId: ProductId
): Promise<Result<ProblemDetails, StoreInventoryItem>> => {
  const response = await get(
    `/v1/stores/${storeId}/products/${productId}/reports`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreInventoryItem);
  }

  return err(content as ProblemDetails);
};

export const add = async (
  storeId: string,
  data: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(`/v1/stores/${storeId}/products`, data);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const update = async (
  storeId: string,
  data: any
): Promise<Result<ProblemDetails, StoreInventoryItem>> => {
  const response = await put(
    `/v1/stores/${storeId}/products/${data.productId}`,
    data
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreInventoryItem);
  }

  return err(content as ProblemDetails);
};

export const syncPriceTag = async (
  storeId: string,
  productId: ProductId
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(
    `/v1/stores/${storeId}/products/${productId}/sync`
  );

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const saveSpoilageReport = async (
  storeId: string,
  productId: ProductId,
  data: ProductSpoilageReport
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(
    `/v1/stores/${storeId}/products/${productId}/spoilage`,
    data
  );

  const content = await response.json();

  if (response.ok) {
    return ok(content as string);
  }

  return err(content as ProblemDetails);
};

export const remove = async (
  storeId: string,
  productId: ProductId
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await del(`/v1/stores/${storeId}/products/${productId}`);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const removeReportedProductSpoilages = async (
  ids: string[]
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await del(`/v1/stores/any/product-spoilage`, {}, ids);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const removeReportedProductStocktaking = async (
  id: string
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await del(`/v1/stores/any/product-stocktaking`, {}, id);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const removeMany = async (
  storeId: string,
  productIds: ReadonlyArray<ProductId>
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await del(
    `/v1/stores/${storeId}/products/many`,
    {},
    { productIds }
  );

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const reportDeliveryFile = async (
  storeId: string,
  file: Blob
): Promise<Result<ProblemDetails, any>> => {
  const response = await upload(`/v1/stores/${storeId}/product-delivery`, file);

  const content = await response.json();

  if (response.ok) {
    return ok(content);
  }

  return err(content as ProblemDetails);
};
